import {
  filledPortalTableRows,
  formatPortalAmount,
  isPortalAmount,
  parsePortalAmount,
  portalAmountCurrency,
  portalTableMaxRows,
  type PortalAnswerValue,
  type PortalTableRows,
} from "@ledgerbox/domain"

/* What the answer controls for an amount, a table, a checklist and a
   choice with several answers do with the client's input. Plain logic, so
   every rule is testable; item-answer-fields.tsx draws it. */

/** The picks an answer holds: the ticked to-dos or the chosen choices. */
export const pickedOf = (value: PortalAnswerValue | undefined): string[] =>
  Array.isArray(value) &&
  value.every((entry: unknown) => typeof entry === "string")
    ? [...(value as readonly string[])]
    : []

/**
 * The picks after one is pressed, in the firm's order. Null when none is
 * left, which clears the answer.
 */
export const togglePick = (
  options: readonly string[],
  picked: readonly string[],
  option: string
): string[] | null => {
  const next = new Set(picked)
  if (next.has(option)) next.delete(option)
  else next.add(option)
  const ordered = options.filter((entry) => next.has(entry))
  return ordered.length ? ordered : null
}

/** The currency sign in front of an amount field, e.g. "$". */
export const amountSign = (currency: string = portalAmountCurrency): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency })
    .formatToParts(0)
    .find((part) => part.type === "currency")?.value ?? currency

/** What the Send button does with the amount field: send it, or say why
 * it cannot. The answer already sent is compared as a number, so "40" and
 * "40.00" are the same answer. */
export const amountToSend = (
  text: string,
  answered: string
):
  | { kind: "send"; value: string }
  | { kind: "same" }
  | { kind: "invalid"; problem: string }
  | { kind: "empty" } => {
  if (!text.trim()) return { kind: "empty" }
  const value = parsePortalAmount(text)
  if (value === null)
    return {
      kind: "invalid",
      problem: /\.\d{3,}\s*$/.test(text)
        ? "Use 2 numbers for the cents, for example 12.35"
        : "Enter the amount in numbers, for example 1250.00",
    }
  if (isPortalAmount(answered) && Number(answered) === Number(value))
    return { kind: "same" }
  return { kind: "send", value }
}

/** What the amount field shows: the answer as the firm reads it, with its
 * cents and separators ("1,250.50"), while the field holds the answer
 * untouched; the client's own typing as it is once they change it, so a
 * deleted digit never comes back. */
export const amountFieldText = (text: string, answered: string): string =>
  text === answered && isPortalAmount(answered)
    ? new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(answered))
    : text

/** A line under an amount below zero, so a stray minus sign is seen before
 * the firm reads it. Null for any other amount. */
export const amountMinusNote = (
  text: string,
  currency?: string
): string | null => {
  const value = parsePortalAmount(text)
  return value !== null && Number(value) < 0
    ? `This is a minus amount: ${formatPortalAmount(value, currency)}`
    : null
}

/** A row with a blank cell under every column. */
export const blankTableRow = (columns: readonly string[]): string[] =>
  columns.map(() => "")

/** The rows the table shows: the client's rows, or one blank row to start.
 * Each row has one cell per column the item has now, so a column the firm
 * added takes typing and a column it took off is not sent. */
export const tableRowsToShow = (
  rows: PortalTableRows,
  columns: readonly string[]
): PortalTableRows =>
  rows.length
    ? rows.map((cells) => columns.map((_, index) => cells[index] ?? ""))
    : [blankTableRow(columns)]

export const withTableCell = (
  rows: PortalTableRows,
  at: { row: number; column: number },
  text: string
): string[][] =>
  rows.map((cells, rowIndex) =>
    cells.map((cell, columnIndex) =>
      rowIndex === at.row && columnIndex === at.column ? text : cell
    )
  )

export const withTableRow = (
  rows: PortalTableRows,
  columns: readonly string[]
): string[][] =>
  rows.length >= portalTableMaxRows
    ? rows.map((cells) => [...cells])
    : [...rows.map((cells) => [...cells]), blankTableRow(columns)]

export const withoutTableRow = (
  rows: PortalTableRows,
  index: number
): string[][] =>
  rows.filter((_, rowIndex) => rowIndex !== index).map((cells) => [...cells])

export const canAddTableRow = (rows: PortalTableRows): boolean =>
  rows.length < portalTableMaxRows

/** The rows Send posts, without the blank ones. Null when nothing is filled
 * in, so the button stays off. */
export const tableRowsToSend = (rows: PortalTableRows): string[][] | null => {
  const filled = filledPortalTableRows(rows)
  return filled.length ? filled : null
}
